/* file description */

import * as React from 'react';
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useImperativeHandle,
} from 'react';
import SPGroupForm from '@/components/SPGroupForm';
import { SPTabList } from '@/components/SPTabList';
import useMetaStore from '@/store/useMetaStore';
import useValuesStore from '@/store/useValuesStore';
import styles from './index.module.less';
import { CascadePageProps } from './type';
import {
  useSPFormWithModelSchema,
  useFreeComponents,
  useLoad,
  useEventCenter,
  useRuleOfChange,
} from './effect';
import { Layout, Typography } from 'antd';
import { SPCascadeBtn } from '@/components/SPCascadeBtn';
import _ from 'lodash';
import { SPStepsWithApi } from '@/components/SPSteps';
import { SPTableList } from '@/components/SPTableList';
import { DrawerLayout, PageLayout } from './components';
import { useFreeBtnClick } from '@/components/SPTableList/effect';
import SpQiankun from '@/components/sp-qiankun';
import { useSaveEvent } from '@/components/SPCascadeBtn/effect/useSaveEvent';

const { Header, Content, Footer, Sider } = Layout;
const { Title } = Typography;

const CascadePage: React.FC<CascadePageProps> = (props) => {
  const [collapsed, setCollapsed] = useState(false);
  const [siderWidth, setSiderWidth] = useState(300);
  const headFormWithRef = useRef<any>();
  const refList = useRef<Map<string, React.MutableRefObject<any>>>(new Map());
  const sotreState = useMetaStore();
  const {
    taskId,
    recordId,
    utils,
    taskInfo,
    pageType,
    pageConfig,
    isMobile,
    moduleList,
    taskInfo: { mainModuleId },
    formRef,
  } = sotreState;
  const store = useValuesStore();
  const { values, setValues, resetValue, realoadValue } = store;

  // 步骤条是否展示
  const stepsShow = useMemo(() => {
    const mainModule = moduleList?.find(
      (moduleItem: Record<string, any>) => moduleItem.moduleID === mainModuleId,
    );

    const mainModuleHandle = mainModule?.handle;

    // 是否数据被删 即主模型的下的values数据都是空
    const noValue = Object.values(values?.[mainModuleHandle] ?? {}).every(
      (value) => value === null || value === undefined,
    );

    return (
      taskInfo.flag === 'true' && recordId && pageType === 'view' && !noValue
    );
  }, [taskInfo, recordId, pageType, moduleList, mainModuleId, values]);

  const spRecordSchema = useSPFormWithModelSchema({
    schemaList: taskInfo.relationTree[0],
    metaInfoByRule: sotreState.metaInfoByRule,
  });

  const { extralComponents } = useFreeComponents({
    relationSchema: taskInfo.relationTree[0],
    refList,
  });

  // 自定义按钮点击事件
  const { freeButtonClick } = useFreeBtnClick({ refList });
  // 事件中心
  const { eventQiankunState, setEventQiankunState } = useEventCenter();
  // 规则change
  useRuleOfChange();
  // 页面初始化loading
  const { loadingHolder } = useLoad({
    version: values?.[spRecordSchema.props.moduleID]?.system_version,
  });

  useEffect(() => {
    // 收集ref
    refList.current.set(
      `${spRecordSchema.props.moduleID},head`,
      headFormWithRef.current,
    );
    /**
     * TODO: 目前只支持了新增和编辑页面
     * 触发页面mouted的js脚本
     */
    if (pageConfig?.createPageConfig?.pageMountedJs) {
      freeButtonClick({
        btnInfo: {
          freeJs: pageConfig?.createPageConfig?.pageMountedJs,
        },
        open: undefined,
      });
    }
  }, []);

  useEffect(() => {
    utils?.queryParams?.SHOWTYPE !== 'modal' &&
      utils?.buiNavigate({
        type: 'updateTabName',
        title: spRecordSchema?.title,
      });
    if (isMobile) {
      document.title = spRecordSchema?.title;
    }
  }, [spRecordSchema?.title]);

  // 基于规则生成的meta信息过滤模型
  const metaInfoByRuleFilter = (children: any[]) => {
    return (children || []).filter((model) => {
      const { metaInfoByRule } = sotreState;
      if (_.get(metaInfoByRule, [model.code, 'visible']) === false) {
        return false;
      }
      return true;
    });
  };
  const { validateForm } = useSaveEvent({ refList });
  // ref转发
  useImperativeHandle(formRef, () => ({
    validateForm,
  }));

  return (
    <>
      <PageLayout collapsed={collapsed}>
        <Content slotName={spRecordSchema.title}>
          <div>
            {!isMobile && spRecordSchema?.tabNameShow && (
              <Title level={2}>{spRecordSchema?.title}</Title>
            )}
            <SPGroupForm
              {...spRecordSchema.props}
              formRef={headFormWithRef}
              value={_.get(values, spRecordSchema.props.moduleID)}
              onValuesChange={(chaneValues, allValues) => {
                setValues(spRecordSchema.props.moduleID, {
                  ...values[spRecordSchema.props.moduleID],
                  ...allValues,
                });
              }}
              extral={extralComponents}
              storeValue={values}
              store={store}
              isMobile={isMobile}
            />
          </div>
          {taskInfo.relationTree[0]?.children &&
            (taskInfo?.maxTag > 2 ? (
              metaInfoByRuleFilter(
                taskInfo.relationTree[0]?.children || [],
              ).map((treeNow, index) => (
                <SPTabList
                  schema={treeNow}
                  key={treeNow.code}
                  refList={refList}
                />
              ))
            ) : (
              <SPTableList
                schema={taskInfo.relationTree[0]?.children}
                refList={refList}
              />
            ))}
        </Content>
        {/* {stepsShow && (
          <Sider
            style={{
              background: '#fff',
              marginLeft: '10px',
              display: collapsed ? 'block' : 'none',
            }}
            width={siderWidth}
            slotName="审批"
          > */}
        <SPStepsWithApi
          slotName="审批"
          taskId={taskId}
          dataId={recordId || ''}
          onSuccess={() => {
            realoadValue(taskInfo.relationTree[0]?.code);
          }}
          onCloseStep={(value: boolean) => {
            setCollapsed(value);
          }}
          onFold={(value: boolean) => {
            setSiderWidth(value ? 40 : 300);
          }}
          version={values?.[spRecordSchema.props.moduleID]?.system_version}
          collapsed={collapsed}
          siderWidth={siderWidth}
          moduleList={moduleList}
          mainModuleId={mainModuleId}
          values={values}
          taskInfo={taskInfo}
          recordId={recordId}
          pageType={pageType}
          refList={refList}
          utils={utils}
          // version={utils?.queryParams?.version}
        />
        {/* </Sider>
        )} */}
      </PageLayout>
      <DrawerLayout
        placement="right"
        onClose={() => {
          setEventQiankunState({ eventQiankunVisible: false });
        }}
        visible={eventQiankunState.eventQiankunVisible}
        contentWrapperStyle={{ minWidth: '70%' }}
        destroyOnClose
        style={{
          overflow: 'hidden',
        }}
      >
        <SpQiankun
          qiankun={utils.qiankun}
          routeUrl={eventQiankunState.routeUrl}
          saveCb={() => {}}
        />
      </DrawerLayout>
      {spRecordSchema?.submitBtnShow && <SPCascadeBtn refList={refList} />}

      {loadingHolder}
    </>
  );
};
CascadePage.displayName = 'SPCentre';

export default CascadePage;
